export type Language = "he" | "en" | "ru";

export const languageNames: Record<Language, string> = {
  he: "עברית",
  en: "English",
  ru: "Русский",
};

export const languageFlags: Record<Language, string> = {
  he: "il",
  en: "us",
  ru: "ru",
};

export const languageDir: Record<Language, "rtl" | "ltr"> = {
  he: "rtl",
  en: "ltr",
  ru: "ltr",
};

type TranslationKeys = {
  // Header
  searchPlaceholder: string;
  login: string;
  signup: string;
  logout: string;
  admin: string;
  credits: string;
  orders: string;
  topUpCredits: string;

  // Hero
  welcomeTo: string;
  brandName: string;
  heroDesc: string;
  askQuestion: string;
  smmLabel: string;
  heroTitle: string;
  heroDesc2: string;
  heroExperience: string;

  // Sections
  ourPlatforms: string;
  products: string;
  whyChooseUs: string;
  customerReviews: string;
  showAllReviews: string;
  moreDetails: string;
  rating: string;

  // Features
  feature1Title: string;
  feature1Desc: string;
  feature2Title: string;
  feature2Desc: string;
  feature3Title: string;
  feature3Desc: string;
  feature4Title: string;
  feature4Desc: string;

  // Cart
  shoppingCart: string;
  clearAll: string;
  cartEmpty: string;
  continueShopping: string;
  total: string;
  goToCheckout: string;
  addToCart: string;
  addedToCart: string;

  // Footer
  platforms: string;
  links: string;
  whyUs: string;
  footerDesc: string;
  safeService: string;
  autoStart: string;
  securePayment: string;
  paymentMethods: string;
  allRightsReserved: string;
  customerReviewsLink: string;
  cookiePolicy: string;

  // Checkout
  checkout: string;
  home: string;
  completeOrder: string;
  personalDetails: string;
  fullName: string;
  email: string;
  phone: string;
  profileLink: string;
  notesOptional: string;
  continueToPayment: string;
  securePaymentTitle: string;
  orderSummary: string;
  items: string;
  totalToPay: string;
  cartEmptyCheckout: string;
  addProductsFirst: string;
  backToStore: string;
  paymentSuccess: string;
  thankYou: string;
  confirmationEmail: string;
  backToHome: string;

  // Auth modals
  loginTitle: string;
  signupTitle: string;
  resetPassword: string;
  emailOnlyLogin: string;
  emailOnlyDesc: string;
  sendLoginLink: string;
  sending: string;
  forgotPassword: string;
  backToLogin: string;
  continueWithGoogle: string;
  continueWithApple: string;
  signupWithGoogle: string;
  signupWithApple: string;
  or: string;
  noAccount: string;
  createAccount: string;
  haveAccount: string;
  password: string;
  confirmPasswordLabel: string;
  loggingIn: string;
  creatingAccount: string;
  enterEmailReset: string;
  sendResetLink: string;
  checkEmail: string;
  resetLinkSent: string;

  // Categories
  categories: string;

  // Product page
  enterFullName: string;
  enterEmail: string;
  enterPhone: string;
  enterProfileLink: string;
  notesPlaceholder: string;
  preparingPayment: string;
  productNotFound: string;
  addedToCartMsg: string;

  // TopUp page
  topUpTitle: string;
  topUpDesc: string;
  currentBalance: string;
  basic: string;
  standard: string;
  popular: string;
  premium: string;
  mostPopular: string;
  bonus: string;
  totalCredits: string;
  customAmount: string;
  enterAmount: string;
  minimum20: string;
  payment: string;
  freeBonus: string;
  bonusTable: string;
  securePaymentDesc: string;
  instantTopUp: string;
  instantTopUpDesc: string;
  freeBonusDesc: string;
  topUpCta: string;
  loadCredits: string;
  topUpSuccess: string;
  creditsLoaded: string;
  toStore: string;
  loadMore: string;
  loginRequired: string;
  loginToTopUp: string;
  connect: string;
  paymentError: string;
  minTopUpError: string;
  loadAndStart: string;
  creditInfo: string;
  loadNow: string;
  securePaymentCardcom: string;
  customer: string;

  // Misc
  pageNotFound: string;

  // SEO blocks
  seoBlock1Title: string;
  seoBlock1Text: string;
  seoBlock2Title: string;
  seoBlock2Text: string;
  seoBlock3Title: string;
  seoBlock3Text: string;
  seoBlock4Title: string;
  seoBlock4Text: string;
  seoBlock5Title: string;
  seoBlock5Text: string;
};

const translations: Record<Language, TranslationKeys> = {
  he: {
    searchPlaceholder: "חפש שירות...",
    login: "התחברות",
    signup: "הרשמה",
    logout: "התנתקות",
    admin: "ניהול",
    welcomeTo: "ברוכים הבאים ל",
    brandName: "מחוברים",
    heroDesc: "קידום מקצועי ובטוח ברשתות חברתיות - לייקים ועוקבים ברשתות חברתיות פופולריות. כיום, הפעילות של חשבון עסקי או אישי ברשתות חברתיות יכולה להביא לא רק תחושת שמחה על הפופולריות שלו, אלא גם כסף אמיתי ופרסום. עם השימוש בשירותים איכותיים ובטוחים לחלוטין שלנו, תוכלו להגדיל את המדדים בפרופילים של כל הרשתות החברתיות.",
    askQuestion: "○ שאל שאלה",
    smmLabel: "שיווק ברשתות חברתיות",
    heroTitle: "קנה עוקבים, לייקים, צפיות ותגובות ברשתות חברתיות",
    heroDesc2: "שירות הקידום האוטומטי שלנו ברשתות חברתיות יאפשר לכם להשיג את התוצאה הרצויה במהירות, תוך הגדלה משמעותית של הפעילות בחשבון או בקהילה הרצויים. לא פעם קידום מקצועי שכזה גורם לאפקט כדור שלג, ומושך משתמשים אמיתיים.",
    heroExperience: "אנחנו צוות עם יותר מ-7 שנות ניסיון ברשתות חברתיות. כבר עזרנו לאלפי לקוחות, ואנחנו יכולים לעזור גם לך.",
    ourPlatforms: "הפלטפורמות שלנו",
    products: "מוצרים",
    whyChooseUs: "למה לבחור בנו?",
    customerReviews: "ביקורות לקוחות",
    showAllReviews: "הצג את כל הביקורות",
    moreDetails: "פרטים נוספים ←",
    rating: "דירוג",
    feature1Title: "איכות ומחיר 24/7",
    feature1Desc: "היו בטוחים שיש לנו את המחירים הטובים ביותר עבור שירותים באיכות הגבוהה ביותר.",
    feature2Title: "אמינות",
    feature2Desc: "אוטומציה מלאה של הפעלה וביצוע כל השירותים במהירות הבטוחה ביותר.",
    feature3Title: "תמיכה 24/7",
    feature3Desc: "נשתדל לעזור לכם במהירות בפניות או בצ'אט כמעט בכל שעה ביום.",
    feature4Title: "הניסיון שלנו",
    feature4Desc: "אנחנו מקדמים ברשתות חברתיות כבר יותר מ-7 שנים והשלמנו יותר מ-13 מיליון הזמנות.",
    shoppingCart: "סל קניות",
    clearAll: "נקה הכל",
    cartEmpty: "הסל ריק",
    continueShopping: "המשך בקניות",
    total: "סה״כ:",
    goToCheckout: "מעבר לתשלום",
    addToCart: "הוסף לסל",
    addedToCart: "נוסף לסל",
    platforms: "פלטפורמות",
    links: "קישורים",
    whyUs: "למה אנחנו?",
    footerDesc: "קידום מקצועי ובטוח ברשתות חברתיות. יותר מ-7 שנות ניסיון ולמעלה מ-13 מיליון הזמנות שהושלמו.",
    safeService: "שירות בטוח ומאובטח ב-100%",
    autoStart: "התחלת עבודה אוטומטית 24/7",
    securePayment: "תשלום מאובטח בכרטיס אשראי וקריפטו",
    paymentMethods: "אמצעי תשלום:",
    allRightsReserved: "כל הזכויות שמורות.",
    customerReviewsLink: "⭐ ביקורות לקוחות",
    cookiePolicy: "🍪 מדיניות עוגיות",
    checkout: "תשלום",
    home: "דף הבית",
    completeOrder: "השלמת הזמנה",
    personalDetails: "פרטים אישיים",
    fullName: "שם מלא",
    email: "אימייל",
    phone: "טלפון",
    profileLink: "קישור לפרופיל / פוסט",
    notesOptional: "הערות (אופציונלי)",
    continueToPayment: "המשך לתשלום",
    securePaymentTitle: "תשלום מאובטח",
    orderSummary: "סיכום הזמנה",
    items: "פריטים",
    totalToPay: "סה״כ לתשלום:",
    cartEmptyCheckout: "הסל ריק",
    addProductsFirst: "הוסף מוצרים לסל לפני שתמשיך לתשלום",
    backToStore: "חזרה לחנות",
    paymentSuccess: "התשלום בוצע בהצלחה!",
    thankYou: "תודה על הרכישה שלך ב-mehubarim.co.il",
    confirmationEmail: "נשלח לך אישור לאימייל שהזנת. ההזמנה תתחיל לפעול בהקדם.",
    backToHome: "חזרה לדף הבית",
    loginTitle: "התחברות",
    signupTitle: "הרשמה",
    resetPassword: "איפוס סיסמה",
    emailOnlyLogin: "התחברות עם אימייל",
    emailOnlyDesc: "נשלח לך קישור התחברות ישירות לאימייל, ללא צורך בסיסמה.",
    sendLoginLink: "שלח קישור התחברות",
    sending: "שולח...",
    forgotPassword: "שכחת סיסמה?",
    backToLogin: "חזרה להתחברות",
    continueWithGoogle: "המשך עם Google",
    continueWithApple: "המשך עם Apple",
    signupWithGoogle: "הרשמה עם Google",
    signupWithApple: "הרשמה עם Apple",
    or: "או",
    noAccount: "אין לך חשבון?",
    createAccount: "צור חשבון",
    haveAccount: "כבר יש לך חשבון?",
    password: "סיסמה",
    confirmPasswordLabel: "אימות סיסמה",
    loggingIn: "מתחבר...",
    creatingAccount: "יוצר חשבון...",
    enterEmailReset: "הזן את האימייל שלך ונשלח לך קישור לאיפוס.",
    sendResetLink: "שלח קישור לאיפוס",
    checkEmail: "בדוק את האימייל שלך",
    resetLinkSent: "שלחנו לך קישור לאיפוס סיסמה.",
    categories: "קטגוריות",
    enterFullName: "הזן את שמך המלא",
    enterEmail: "example@email.com",
    enterPhone: "050-1234567",
    enterProfileLink: "https://instagram.com/your-profile",
    notesPlaceholder: "הערות נוספות להזמנה...",
    preparingPayment: "מכין תשלום...",
    productNotFound: "המוצר לא נמצא",
    addedToCartMsg: "נוסף לסל",
    credits: "קרדיטים",
    orders: "הזמנות",
    topUpCredits: "טעינת קרדיטים",
    topUpTitle: "טעינת קרדיטים",
    topUpDesc: "ככל שטוענים יותר, מקבלים יותר! בחרו חבילה ותיהנו מבונוס קרדיטים חינם.",
    currentBalance: "יתרה נוכחית:",
    basic: "בסיסי",
    standard: "סטנדרטי",
    popular: "פופולרי",
    premium: "פרימיום",
    mostPopular: "הכי פופולרי",
    bonus: "בונוס",
    totalCredits: "סה\"כ קרדיטים:",
    customAmount: "סכום מותאם",
    enterAmount: "הזן סכום...",
    minimum20: "מינימום ₪20",
    payment: "תשלום",
    freeBonus: "בונוס חינם",
    bonusTable: "טבלת בונוסים",
    securePaymentDesc: "סליקה מוגנת SSL דרך Cardcom",
    instantTopUp: "טעינה מיידית",
    instantTopUpDesc: "הקרדיטים נטענים ברגע שהתשלום מאושר",
    freeBonusDesc: "ככל שטוענים יותר מקבלים יותר!",
    topUpCta: "טען",
    loadCredits: "טען קרדיטים",
    topUpSuccess: "הטעינה בוצעה בהצלחה!",
    creditsLoaded: "קרדיטים נטענו",
    toStore: "לחנות",
    loadMore: "טען עוד",
    loginRequired: "יש להתחבר כדי לטעון קרדיטים",
    loginToTopUp: "יש להתחבר כדי לטעון קרדיטים",
    connect: "התחבר",
    paymentError: "שגיאה בתשלום",
    minTopUpError: "סכום טעינה מינימלי הוא ₪20",
    loadAndStart: "טען קרדיטים ותתחיל לקנות!",
    creditInfo: "1 קרדיט = ₪1 | מינימום טעינה ₪20",
    loadNow: "טען עכשיו",
    securePaymentCardcom: "תשלום מאובטח",
    customer: "לקוח",
    seoBlock1Title: "קנה לייקים באינסטגרם",
    seoBlock1Text: "יש לך עוקבים רבים באינסטגרם אבל המעורבות של הקהל משאירה מקום לשיפור? קנה עוקבים, לייקים ותגובות באינסטגרם כחבילה אחת או שלב מספר אפשרויות שאנחנו מציעים.",
    seoBlock2Title: "קנה צפיות ביוטיוב",
    seoBlock2Text: "יוטיוב הוא מקום מצוין להרוויח כסף אם אתה יודע איך ליצור תוכן מעניין ויראלי. כאן תוכל לקנות צפיות ביוטיוב בזול כדי להניע את הערוץ שלך.",
    seoBlock3Title: "קנה מנויים ביוטיוב",
    seoBlock3Text: "כל ערוץ יוטיוב צריך כמה שיותר מנויים. ערבב את המנויים האורגניים שלך עם כאלה בתשלום כדי ליצור אפקט כדור שלג.",
    seoBlock4Title: "קנה עוקבים בטוויטר",
    seoBlock4Text: "טוויטר הוא המקום הטוב ביותר עבור פרילנסרים, בלוגרים וחובבי קריפטו. קנה עוקבים ולייקים לחשבון הטוויטר שלך!",
    seoBlock5Title: "קנה לייקים בפייסבוק",
    seoBlock5Text: "פייסבוק הוא אחד המקומות הטובים ביותר לקדם את הבלוג או העסק שלך. קנה עוקבים, לייקים ותגובות כדי לשפר את הנראות שלך.",
    pageNotFound: "הדף לא נמצא",
  },

  en: {
    searchPlaceholder: "Search service...",
    login: "Login",
    signup: "Sign Up",
    logout: "Logout",
    admin: "Admin",
    welcomeTo: "Welcome to ",
    brandName: "Mehubarim",
    heroDesc: "Professional and safe promotion on social networks: likes and followers on popular social media platforms. Today, activity on a business or personal social media account can bring not only joy from popularity but also real money and publicity. With our high-quality and completely safe services, you can boost metrics across all social networks.",
    askQuestion: "○ Ask a Question",
    smmLabel: "Social Media Marketing",
    heroTitle: "Buy followers, likes, views and comments on social media",
    heroDesc2: "Our automated social media promotion service will help you achieve the desired result quickly, while significantly increasing activity on the desired account or community. Professional promotion often creates a snowball effect, attracting real users.",
    heroExperience: "We are a team with over 7 years of experience in social media. We have already helped thousands of clients, and we can help you too.",
    ourPlatforms: "Our Platforms",
    products: "products",
    whyChooseUs: "Why Choose Us?",
    customerReviews: "Customer Reviews",
    showAllReviews: "Show All Reviews",
    moreDetails: "More details →",
    rating: "Rating",
    feature1Title: "Quality & Price 24/7",
    feature1Desc: "Rest assured we have the best prices for the highest quality services.",
    feature2Title: "Reliability",
    feature2Desc: "Full automation of activation and execution of all services at the safest speed.",
    feature3Title: "Support 24/7",
    feature3Desc: "We will do our best to help you quickly via tickets or chat almost any time of day.",
    feature4Title: "Our Experience",
    feature4Desc: "We have been promoting on social media for over 7 years and completed more than 13 million orders.",
    shoppingCart: "Shopping Cart",
    clearAll: "Clear All",
    cartEmpty: "Cart is empty",
    continueShopping: "Continue Shopping",
    total: "Total:",
    goToCheckout: "Go to Checkout",
    addToCart: "Add to Cart",
    addedToCart: "added to cart",
    platforms: "Platforms",
    links: "Links",
    whyUs: "Why Us?",
    footerDesc: "Professional and safe social media promotion. Over 7 years of experience and more than 13 million completed orders.",
    safeService: "100% safe and secure service",
    autoStart: "Automatic start 24/7",
    securePayment: "Secure payment by credit card and crypto",
    paymentMethods: "Payment methods:",
    allRightsReserved: "All rights reserved.",
    customerReviewsLink: "⭐ Customer Reviews",
    cookiePolicy: "🍪 Cookie Policy",
    checkout: "Checkout",
    home: "Home",
    completeOrder: "Complete Order",
    personalDetails: "Personal Details",
    fullName: "Full Name",
    email: "Email",
    phone: "Phone",
    profileLink: "Profile / Post Link",
    notesOptional: "Notes (optional)",
    continueToPayment: "Continue to Payment",
    securePaymentTitle: "Secure Payment",
    orderSummary: "Order Summary",
    items: "items",
    totalToPay: "Total to pay:",
    cartEmptyCheckout: "Cart is empty",
    addProductsFirst: "Add products to cart before proceeding to checkout",
    backToStore: "Back to Store",
    paymentSuccess: "Payment Successful!",
    thankYou: "Thank you for your purchase at mehubarim.co.il",
    confirmationEmail: "A confirmation will be sent to your email. Your order will be processed shortly.",
    backToHome: "Back to Home",
    loginTitle: "Login",
    signupTitle: "Sign Up",
    resetPassword: "Reset Password",
    emailOnlyLogin: "Email Login",
    emailOnlyDesc: "We'll send you a login link directly to your email, no password needed.",
    sendLoginLink: "Send Login Link",
    sending: "Sending...",
    forgotPassword: "Forgot password?",
    backToLogin: "Back to Login",
    continueWithGoogle: "Continue with Google",
    continueWithApple: "Continue with Apple",
    signupWithGoogle: "Sign up with Google",
    signupWithApple: "Sign up with Apple",
    or: "or",
    noAccount: "Don't have an account?",
    createAccount: "Create Account",
    haveAccount: "Already have an account?",
    password: "Password",
    confirmPasswordLabel: "Confirm Password",
    loggingIn: "Logging in...",
    creatingAccount: "Creating account...",
    enterEmailReset: "Enter your email and we'll send you a reset link.",
    sendResetLink: "Send Reset Link",
    checkEmail: "Check your email",
    resetLinkSent: "We've sent you a password reset link.",
    categories: "Categories",
    enterFullName: "Enter your full name",
    enterEmail: "example@email.com",
    enterPhone: "050-1234567",
    enterProfileLink: "https://instagram.com/your-profile",
    notesPlaceholder: "Additional notes for the order...",
    preparingPayment: "Preparing payment...",
    productNotFound: "Product not found",
    addedToCartMsg: "added to cart",
    credits: "Credits",
    orders: "Orders",
    topUpCredits: "Top Up Credits",
    topUpTitle: "Top Up Credits",
    topUpDesc: "The more you load, the more you get! Choose a package and enjoy free bonus credits.",
    currentBalance: "Current balance:",
    basic: "Basic",
    standard: "Standard",
    popular: "Popular",
    premium: "Premium",
    mostPopular: "Most Popular",
    bonus: "Bonus",
    totalCredits: "Total credits:",
    customAmount: "Custom Amount",
    enterAmount: "Enter amount...",
    minimum20: "Minimum ₪20",
    payment: "Payment",
    freeBonus: "Free Bonus",
    bonusTable: "Bonus Tiers",
    securePaymentDesc: "SSL-secured payment via Cardcom",
    instantTopUp: "Instant Top-Up",
    instantTopUpDesc: "Credits are loaded as soon as payment is approved",
    freeBonusDesc: "The more you load, the more you get!",
    topUpCta: "Load",
    loadCredits: "Load Credits",
    topUpSuccess: "Top-up successful!",
    creditsLoaded: "credits loaded",
    toStore: "To Store",
    loadMore: "Load More",
    loginRequired: "Login required to top up credits",
    loginToTopUp: "Please login to top up credits",
    connect: "Login",
    paymentError: "Payment error",
    minTopUpError: "Minimum top-up amount is ₪20",
    loadAndStart: "Load credits and start buying!",
    creditInfo: "1 Credit = ₪1 | Minimum top-up ₪20",
    loadNow: "Load Now",
    securePaymentCardcom: "Secure Payment",
    customer: "Customer",
    seoBlock1Title: "Buy Instagram Likes",
    seoBlock1Text: "Have many Instagram followers but low engagement? Buy followers, likes and comments as a bundle or mix options we offer.",
    seoBlock2Title: "Buy YouTube Views",
    seoBlock2Text: "YouTube is a great place to earn money if you know how to create engaging content. Buy YouTube views cheaply to boost your channel.",
    seoBlock3Title: "Buy YouTube Subscribers",
    seoBlock3Text: "Every YouTube channel needs as many subscribers as possible. Mix organic subscribers with paid ones to create a snowball effect.",
    seoBlock4Title: "Buy Twitter Followers",
    seoBlock4Text: "Twitter is the best place for freelancers, bloggers and crypto enthusiasts. Buy followers and likes for your Twitter account!",
    seoBlock5Title: "Buy Facebook Likes",
    seoBlock5Text: "Facebook is one of the best places to promote your blog or business. Buy followers, likes and comments to improve your visibility.",
    pageNotFound: "Page Not Found",
  },

  ru: {
    searchPlaceholder: "Поиск услуги...",
    login: "Вход",
    signup: "Регистрация",
    logout: "Выйти",
    admin: "Админ",
    welcomeTo: "Добро пожаловать в ",
    brandName: "Mehubarim",
    heroDesc: "Профессиональное и безопасное продвижение в социальных сетях: лайки и подписчики в популярных социальных сетях. Сегодня активность бизнес- или личного аккаунта может приносить не только радость от популярности, но и реальные деньги и рекламу. С помощью наших качественных и полностью безопасных услуг вы сможете повысить показатели во всех социальных сетях.",
    askQuestion: "○ Задать вопрос",
    smmLabel: "Маркетинг в социальных сетях",
    heroTitle: "Купить подписчиков, лайки, просмотры и комментарии в социальных сетях",
    heroDesc2: "Наш автоматический сервис продвижения в социальных сетях поможет вам быстро достичь желаемого результата, значительно увеличив активность в нужном аккаунте или сообществе. Профессиональное продвижение часто создаёт эффект снежного кома, привлекая реальных пользователей.",
    heroExperience: "Мы команда с более чем 7-летним опытом в социальных сетях. Мы уже помогли тысячам клиентов, и мы можем помочь и вам.",
    ourPlatforms: "Наши платформы",
    products: "продуктов",
    whyChooseUs: "Почему выбирают нас?",
    customerReviews: "Отзывы клиентов",
    showAllReviews: "Показать все отзывы",
    moreDetails: "Подробнее →",
    rating: "Рейтинг",
    feature1Title: "Качество и цена 24/7",
    feature1Desc: "Будьте уверены, у нас лучшие цены за услуги высочайшего качества.",
    feature2Title: "Надёжность",
    feature2Desc: "Полная автоматизация запуска и выполнения всех услуг на самой безопасной скорости.",
    feature3Title: "Поддержка 24/7",
    feature3Desc: "Мы постараемся помочь вам быстро через тикеты или чат практически в любое время суток.",
    feature4Title: "Наш опыт",
    feature4Desc: "Мы продвигаем в социальных сетях уже более 7 лет и выполнили более 13 миллионов заказов.",
    shoppingCart: "Корзина",
    clearAll: "Очистить всё",
    cartEmpty: "Корзина пуста",
    continueShopping: "Продолжить покупки",
    total: "Итого:",
    goToCheckout: "Перейти к оплате",
    addToCart: "В корзину",
    addedToCart: "добавлено в корзину",
    platforms: "Платформы",
    links: "Ссылки",
    whyUs: "Почему мы?",
    footerDesc: "Профессиональное и безопасное продвижение в социальных сетях. Более 7 лет опыта и более 13 миллионов выполненных заказов.",
    safeService: "100% безопасный и защищённый сервис",
    autoStart: "Автоматический запуск 24/7",
    securePayment: "Безопасная оплата картой и криптовалютой",
    paymentMethods: "Способы оплаты:",
    allRightsReserved: "Все права защищены.",
    customerReviewsLink: "⭐ Отзывы клиентов",
    cookiePolicy: "🍪 Политика cookies",
    checkout: "Оплата",
    home: "Главная",
    completeOrder: "Оформление заказа",
    personalDetails: "Личные данные",
    fullName: "Полное имя",
    email: "Эл. почта",
    phone: "Телефон",
    profileLink: "Ссылка на профиль / пост",
    notesOptional: "Заметки (необязательно)",
    continueToPayment: "Перейти к оплате",
    securePaymentTitle: "Безопасная оплата",
    orderSummary: "Сводка заказа",
    items: "товаров",
    totalToPay: "К оплате:",
    cartEmptyCheckout: "Корзина пуста",
    addProductsFirst: "Добавьте товары в корзину перед оплатой",
    backToStore: "Вернуться в магазин",
    paymentSuccess: "Оплата прошла успешно!",
    thankYou: "Спасибо за покупку на mehubarim.co.il",
    confirmationEmail: "Подтверждение будет отправлено на вашу почту. Ваш заказ начнёт выполняться в ближайшее время.",
    backToHome: "Вернуться на главную",
    loginTitle: "Вход",
    signupTitle: "Регистрация",
    resetPassword: "Сброс пароля",
    emailOnlyLogin: "Вход по email",
    emailOnlyDesc: "Мы отправим вам ссылку для входа на ваш email, без пароля.",
    sendLoginLink: "Отправить ссылку",
    sending: "Отправка...",
    forgotPassword: "Забыли пароль?",
    backToLogin: "Вернуться к входу",
    continueWithGoogle: "Продолжить с Google",
    continueWithApple: "Продолжить с Apple",
    signupWithGoogle: "Регистрация через Google",
    signupWithApple: "Регистрация через Apple",
    or: "или",
    noAccount: "Нет аккаунта?",
    createAccount: "Создать аккаунт",
    haveAccount: "Уже есть аккаунт?",
    password: "Пароль",
    confirmPasswordLabel: "Подтверждение пароля",
    loggingIn: "Входим...",
    creatingAccount: "Создаём аккаунт...",
    enterEmailReset: "Введите ваш email, и мы отправим ссылку для сброса.",
    sendResetLink: "Отправить ссылку для сброса",
    checkEmail: "Проверьте вашу почту",
    resetLinkSent: "Мы отправили вам ссылку для сброса пароля.",
    categories: "Категории",
    enterFullName: "Введите ваше полное имя",
    enterEmail: "example@email.com",
    enterPhone: "050-1234567",
    enterProfileLink: "https://instagram.com/your-profile",
    notesPlaceholder: "Дополнительные заметки к заказу...",
    preparingPayment: "Подготовка оплаты...",
    productNotFound: "Товар не найден",
    addedToCartMsg: "добавлено в корзину",
    credits: "Кредиты",
    orders: "Заказы",
    topUpCredits: "Пополнить кредиты",
    topUpTitle: "Пополнение кредитов",
    topUpDesc: "Чем больше пополняете, тем больше получаете! Выберите пакет и получите бонусные кредиты бесплатно.",
    currentBalance: "Текущий баланс:",
    basic: "Базовый",
    standard: "Стандартный",
    popular: "Популярный",
    premium: "Премиум",
    mostPopular: "Самый популярный",
    bonus: "Бонус",
    totalCredits: "Всего кредитов:",
    customAmount: "Произвольная сумма",
    enterAmount: "Введите сумму...",
    minimum20: "Минимум ₪20",
    payment: "Оплата",
    freeBonus: "Бесплатный бонус",
    bonusTable: "Таблица бонусов",
    securePaymentDesc: "SSL-защищённая оплата через Cardcom",
    instantTopUp: "Мгновенное пополнение",
    instantTopUpDesc: "Кредиты зачисляются сразу после подтверждения оплаты",
    freeBonusDesc: "Чем больше пополняете, тем больше получаете!",
    topUpCta: "Пополнить",
    loadCredits: "Пополнить кредиты",
    topUpSuccess: "Пополнение выполнено успешно!",
    creditsLoaded: "кредитов зачислено",
    toStore: "В магазин",
    loadMore: "Пополнить ещё",
    loginRequired: "Необходимо войти для пополнения кредитов",
    loginToTopUp: "Войдите, чтобы пополнить кредиты",
    connect: "Войти",
    paymentError: "Ошибка оплаты",
    minTopUpError: "Минимальная сумма пополнения: ₪20",
    loadAndStart: "Пополните кредиты и начните покупать!",
    creditInfo: "1 кредит = ₪1 | Минимум пополнения ₪20",
    loadNow: "Пополнить сейчас",
    securePaymentCardcom: "Безопасная оплата",
    customer: "Клиент",
    seoBlock1Title: "Купить лайки в Инстаграм",
    seoBlock1Text: "Много подписчиков в Инстаграм, но мало взаимодействия? Купите подписчиков, лайки и комментарии пакетом или комбинируйте варианты.",
    seoBlock2Title: "Купить просмотры на YouTube",
    seoBlock2Text: "YouTube это отличное место для заработка. Купите просмотры на YouTube дёшево, чтобы раскрутить свой канал.",
    seoBlock3Title: "Купить подписчиков на YouTube",
    seoBlock3Text: "Каждому каналу нужно как можно больше подписчиков. Смешайте органических подписчиков с платными для создания эффекта снежного кома.",
    seoBlock4Title: "Купить подписчиков в Твиттер",
    seoBlock4Text: "Твиттер, лучшее место для фрилансеров, блогеров и крипто-энтузиастов. Купите подписчиков и лайки для аккаунта!",
    seoBlock5Title: "Купить лайки в Фейсбук",
    seoBlock5Text: "Фейсбук, одно из лучших мест для продвижения блога или бизнеса. Купите подписчиков, лайки и комментарии.",
    pageNotFound: "Страница не найдена",
  },
};

export default translations;
